import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Card, CardActionArea, CardContent,
  CircularProgress, Alert, IconButton, Chip
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useClientUiSettings } from '../../hooks/useClientUiSettings';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Client-facing voice picker.
 *
 * Renders a dialog with cards for each voice the client is allowed to choose
 * between (therapist whitelist if populated, platform-default pool otherwise).
 * When voice selection is not permitted, shows a friendly explanation rather
 * than a disabled picker.
 */
const ClientVoiceSelector = ({ token, open, onClose, onVoiceChanged }) => {
  const { selectedRepresentation = null } = useClientUiSettings();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [voices, setVoices] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Voice paired with the active representation, if any. We surface this as a
  // dedicated "use the AI's voice" card so a user who previously overrode
  // their voice can revert without having to know a particular voice name.
  const repVoiceName = selectedRepresentation?.voice?.name || null;
  const repDisplayName = selectedRepresentation?.displayName || null;

  const authHeaders = { headers: { 'x-auth-token': token } };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/client/voice-options`, authHeaders);
      setEnabled(res.data?.enabled !== false);
      setVoices(res.data?.voices || []);
      setSelectedId(res.data?.selectedVoiceCatalogId || null);
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    }
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (open) load(); }, [open, load]);

  const preview = async voice => {
    setPreviewingId(voice._id);
    try {
      // Server resolves the controlled preview text (voice override → platform
      // default → fallback). Fall back to a local string only if absent.
      const text = voice.previewText
        || (voice.clientDescription
          ? `Hi — this is ${voice.name}. ${voice.clientDescription}`
          : `Hi — this is ${voice.name}.`);
      const res = await axios.post(
        `${API_BASE}/tts/generate`,
        { text, voiceId: voice.providerVoiceId },
        authHeaders
      );
      if (res.data?.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
        audio.onended = () => setPreviewingId(null);
        audio.play();
      } else {
        setPreviewingId(null);
      }
    } catch (e) {
      setError(`Preview failed: ${e.response?.data?.msg || e.message}`);
      setPreviewingId(null);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.put(
        `${API_BASE}/client/voice-preference`,
        { voiceCatalogId: selectedId },
        authHeaders
      );
      onVoiceChanged?.(selectedId);
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.msg || e.message);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Choose your voice</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !enabled && (
          <Alert severity="info">
            Voice selection isn't available right now. Your therapist hasn't enabled it for you,
            or the feature is turned off for this service. You'll hear the default voice.
          </Alert>
        )}

        {!loading && enabled && voices.length === 0 && (
          <Typography color="text.secondary">
            No voices available right now. Check back later — your therapist may still be setting
            this up.
          </Typography>
        )}

        {!loading && enabled && voices.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tap a voice to select it. Press the play icon to hear a sample.
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* "Use the AI's voice" — clears the user override (selectedId=null)
                so the resolver falls back to the representation/persona voice.
                Always rendered when voice selection is permitted; highlighted
                when no override is currently in effect. */}
            {(() => {
              const isCleared = selectedId == null;
              const headline = repDisplayName
                ? `Use ${repDisplayName}'s voice`
                : 'Use the recommended voice';
              const subhead = repVoiceName
                ? `${repVoiceName} — the voice paired with this AI's appearance.`
                : "We'll use the voice your therapist set up for this AI.";
              return (
                <Card
                  variant={isCleared ? 'elevation' : 'outlined'}
                  elevation={isCleared ? 3 : 0}
                  sx={{
                    mb: 1.5,
                    borderColor: isCleared ? 'primary.main' : 'divider',
                    borderWidth: isCleared ? 2 : 1,
                    borderStyle: 'solid',
                    bgcolor: isCleared ? 'action.selected' : 'background.paper'
                  }}
                >
                  <CardActionArea onClick={() => setSelectedId(null)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        {isCleared
                          ? <CheckCircleIcon color="primary" fontSize="small" />
                          : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />}
                        <AutoAwesomeIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle1" fontWeight={600}>{headline}</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {subhead}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })()}
            {voices.map(voice => {
              const isSelected = String(selectedId) === String(voice._id);
              return (
                <Card
                  key={voice._id}
                  variant={isSelected ? 'elevation' : 'outlined'}
                  elevation={isSelected ? 3 : 0}
                  sx={{
                    mb: 1.5,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderWidth: isSelected ? 2 : 1,
                    borderStyle: 'solid'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                    <CardActionArea onClick={() => setSelectedId(voice._id)} sx={{ flex: 1 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          {isSelected
                            ? <CheckCircleIcon color="primary" fontSize="small" />
                            : <RadioButtonUncheckedIcon color="disabled" fontSize="small" />}
                          <Typography variant="subtitle1" fontWeight={600}>{voice.name}</Typography>
                        </Box>
                        {voice.clientDescription && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {voice.clientDescription}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {voice.tags?.gender && voice.tags.gender !== 'unspecified' && (
                            <Chip size="small" label={voice.tags.gender} />
                          )}
                          {voice.tags?.accent && <Chip size="small" label={voice.tags.accent} />}
                          {(voice.tags?.style || []).map(s => (
                            <Chip key={s} size="small" variant="outlined" label={s} />
                          ))}
                        </Box>
                      </CardContent>
                    </CardActionArea>
                    <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
                      <IconButton onClick={() => preview(voice)} title="Preview">
                        <PlayCircleOutlineIcon color={previewingId === voice._id ? 'primary' : 'inherit'} />
                      </IconButton>
                    </Box>
                  </Box>
                </Card>
              );
            })}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {enabled && voices.length > 0 && (
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save choice'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ClientVoiceSelector;
